/**
 * Reusable lead-magnet landing-page component.
 * Used by /cheat-sheet and /top-50-questions (and could be wrapped around
 * /free-guide too in the future).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, breadcrumbSchema } from "@/hooks/useJsonLd";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Mail, Download, FileText, ShieldCheck,
} from "lucide-react";

/**
 * @param {object} cfg
 * @param {string} cfg.source — value sent as `source` to /api/leads (e.g. "cheat_sheet")
 * @param {string} cfg.path — current page path for breadcrumb schema (e.g. "/cheat-sheet")
 * @param {string} cfg.eyebrow — small uppercase label above the H1
 * @param {string} cfg.titleText — plain-string title for SEO meta + breadcrumbs (JSX `title` may include spans)
 * @param {React.ReactNode} cfg.title — main H1 (can include JSX for color highlights)
 * @param {string} cfg.lead — paragraph below title
 * @param {Array<{icon, label, sub}>} cfg.bullets — feature bullets in the right column
 * @param {Array<{q, a}>} cfg.faqs — FAQ items
 * @param {Array<{n, title, desc}>} cfg.tocItems — table-of-contents preview of what's in the PDF
 * @param {string} cfg.pdfPath — public-folder path to the actual PDF (e.g. "/lead-magnets/s-98-cheat-sheet.pdf")
 * @param {string} cfg.successHeadline — heading shown after successful signup
 * @param {string} cfg.pageTitle — <title>
 * @param {string} cfg.pageDescription — meta description
 */
export default function LeadMagnetLanding({
  source, path, eyebrow, title, titleText, lead, bullets, faqs, tocItems,
  pdfPath, successHeadline, pageTitle, pageDescription,
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useDocumentMeta({ title: pageTitle, description: pageDescription, ogImage: `/og${path}.png` });
  useJsonLd(breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Free Resources", url: "/" },
    { name: titleText || pageTitle, url: path },
  ]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { email: email.trim().toLowerCase(), full_name: fullName.trim() || null, source });
      setSubmitted(true);
      toast.success("Check your inbox — PDF link sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — try again");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-12" data-testid="leadmagnet-page">
        <div className="overline mb-3"><span className="red-bar" />{eyebrow}</div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* LEFT — Pitch + form */}
          <div className="lg:col-span-3">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1] mb-5" data-testid="lm-headline">{title}</h1>
            <p className="text-lg text-zinc-700 leading-relaxed mb-8 max-w-xl">{lead}</p>

            {submitted ? (
              <div className="card-tech p-6" data-testid="lm-success">
                <CheckCircle2 className="w-10 h-10 text-green-600 mb-3" />
                <h2 className="font-heading text-2xl font-bold mb-2">{successHeadline}</h2>
                <p className="text-sm text-zinc-700 mb-5">
                  Check <strong>{email}</strong>. The PDF link is in the email I just sent.
                  If you don't see it in 5 minutes, check your spam folder.
                </p>
                <a
                  href={pdfPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  data-testid="lm-direct-download"
                >
                  <Download className="w-4 h-4" /> Or download the PDF directly
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="card-tech p-6 max-w-md" data-testid="lm-form">
                <h2 className="font-heading text-xl font-bold mb-1">Where should I send the PDF?</h2>
                <p className="text-xs text-zinc-600 mb-4">Free. No card. Unsubscribe any time.</p>
                <label className="block mb-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">First name (optional)</div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm"
                    placeholder="Trevor"
                    data-testid="lm-name-input"
                  />
                </label>
                <label className="block mb-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Email *</div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm"
                    placeholder="you@example.com"
                    data-testid="lm-email-input"
                  />
                </label>
                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="lm-submit">
                  {loading ? "Sending…" : <>Send me the PDF <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" /> We will never sell your email. Used only for the PDF + 4 follow-up tips over 30 days.
                </p>
              </form>
            )}
          </div>

          {/* RIGHT — bullets */}
          <div className="lg:col-span-2">
            <div className="card-tech p-6 sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-red-600" />
                <h3 className="font-heading text-lg font-bold">What's inside</h3>
              </div>
              <ul className="space-y-3" data-testid="lm-bullets">
                {bullets.map((b) => (
                  <li key={b.label} className="flex items-start gap-2.5 text-sm">
                    <b.icon className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold text-zinc-900">{b.label}</div>
                      {b.sub && <div className="text-xs text-zinc-600 leading-snug mt-0.5">{b.sub}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* TOC preview */}
        {tocItems && tocItems.length > 0 && (
          <section className="mt-20" data-testid="lm-toc">
            <div className="overline mb-3"><span className="red-bar" />Inside the PDF</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">Table of contents</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {tocItems.map((t) => (
                <div key={t.n} className="border-l-4 border-red-600 pl-4 py-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-600 font-bold mb-0.5">Section {t.n}</div>
                  <div className="font-heading text-base font-bold tracking-tight">{t.title}</div>
                  {t.desc && <div className="text-xs text-zinc-700 mt-1 leading-snug">{t.desc}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQs */}
        {faqs && faqs.length > 0 && (
          <section className="mt-20" data-testid="lm-faqs">
            <div className="overline mb-3"><span className="red-bar" />Common questions</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">FAQ</h2>
            <div className="space-y-3 max-w-3xl">
              {faqs.map((f) => (
                <details key={f.q} className="border-2 border-zinc-200 hover:border-red-600 transition-colors">
                  <summary className="cursor-pointer p-4 font-semibold text-sm">{f.q}</summary>
                  <p className="px-4 pb-4 text-sm text-zinc-700 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Cross-links */}
        <section className="mt-20 mb-8" data-testid="lm-cross-links">
          <div className="overline mb-3"><span className="red-bar" />More free resources</div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/free-guide" className={`card-tech p-5 hover:border-red-600 transition-colors ${path === "/free-guide" ? "opacity-40 pointer-events-none" : ""}`}>
              <Mail className="w-5 h-5 text-red-600 mb-2" />
              <h3 className="font-heading text-base font-bold tracking-tight mb-1">25 Free Practice Questions</h3>
              <p className="text-xs text-zinc-600">Interactive — answer in browser. Includes PDF download.</p>
            </Link>
            <Link to="/cheat-sheet" className={`card-tech p-5 hover:border-red-600 transition-colors ${path === "/cheat-sheet" ? "opacity-40 pointer-events-none" : ""}`}>
              <FileText className="w-5 h-5 text-red-600 mb-2" />
              <h3 className="font-heading text-base font-bold tracking-tight mb-1">S-98 Exam Cheat Sheet</h3>
              <p className="text-xs text-zinc-600">10-section PDF. Code book navigation, timing intervals, NYC overrides.</p>
            </Link>
            <Link to="/top-50-questions" className={`card-tech p-5 hover:border-red-600 transition-colors ${path === "/top-50-questions" ? "opacity-40 pointer-events-none" : ""}`}>
              <Download className="w-5 h-5 text-red-600 mb-2" />
              <h3 className="font-heading text-base font-bold tracking-tight mb-1">Top 50 FDNY S-98 Questions</h3>
              <p className="text-xs text-zinc-600">50-question PDF with answers + NFPA citations on every Q.</p>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
